import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRouteDocumentTitle } from '@client/toolkit';
import { ReportingPaths } from '../reporting.router';
import { ReportPageContent } from '../../../components/layout/report-page-content';
import { DashboardFiltersProvider, useDashboardFilters } from '../../dashboard/context/dashboard-filters.context';
import { SectionWrapper } from '../../../components/layout/section-wrapper';
import { DashboardFilters } from '../../dashboard/components';
import { ComparisonChart } from '../../../components/charts/comparison-chart';
import { useGetEosCorrelationQuery } from '@client/api';
import { ComparisonChartTooltip } from '../../../components/charts/tooltip';
import { ChartSectionState } from '../../../components/charts/chart-section-state';
import { eosDateRangeFilterVisiblePresets } from '../../../components/eos/config';

function ConsumptionEnergyByTemperatureSection() {
  const { t } = useTranslation('eos');
  const { selectedSpaceIds, selectedDateFromIso, selectedDateToIso } = useDashboardFilters();

  const { data, isLoading, isError } = useGetEosCorrelationQuery(
    {
      from: selectedDateFromIso,
      to: selectedDateToIso,
      //resolution: 'Hourly' as EosResolution,
      spaceIds: selectedSpaceIds,
    },
    {
      skip: selectedSpaceIds.length === 0 || !selectedDateFromIso || !selectedDateToIso,
    },
  );

  const getTooltipData = (node: any) => {
    const { resolution } = data ?? {};

    const temperature = Number(node.data.x.toFixed(1));
    const consumption = Number(node.data.y.toFixed(1));
    const isOutlier = node.serieId === 'outliers';
    const color = isOutlier ? '#ef4444' : '#00ffc3';
    const timestamp = (node.data as any).timestamp;
    const date = dayjs(timestamp);
    const dateFormatted = date.locale('nb').format('ddd DD, MMM YYYY');

    let formattedDate: string = '';
    if (resolution === 'Hourly') {
      const timeFromFormatted = date.locale('nb').subtract(1, 'hour').format('HH:mm');
      const timeToFormatted = date.locale('nb').format('HH:mm');
      formattedDate = timestamp ? `${dateFormatted} <br/>${timeFromFormatted} - ${timeToFormatted}` : '';
    } else {
      formattedDate = dateFormatted;
    }

    return {
      temperature,
      consumption,
      isOutlier,
      color,
      formattedDate,
    };
  };

  if (selectedSpaceIds && selectedSpaceIds.length === 0) {
    return <ChartSectionState className="aspect-[10/4]">{t('selectBuildingsToView')}</ChartSectionState>;
  }

  if (isLoading) {
    return <ChartSectionState className="aspect-[10/4]">{t('loading')}</ChartSectionState>;
  }

  if (isError) {
    return <ChartSectionState className="aspect-[10/4]">{t('errorLoadingData')}</ChartSectionState>;
  }

  if (!data || !data.points || data.points.length === 0) {
    return <ChartSectionState className="aspect-[10/4]">{t('noDataAvailable')}</ChartSectionState>;
  }

  return (
    <SectionWrapper className="aspect-[10/4]">
      <ComparisonChart
        points={data?.points || []}
        xLabel={t('temperatureOut')}
        yLabel={t('energy')}
        xUnit={data?.xUnit || ''}
        yUnit={data?.yUnit || ''}
        regression={data?.regression || undefined}
        tooltip={({ node }) => {
          const tooltipData = getTooltipData(node);
          return (
            <ComparisonChartTooltip
              label={tooltipData.formattedDate}
              xValue={tooltipData.temperature.toString()}
              xUnit={data?.xUnit || ''}
              yValue={tooltipData.consumption.toString()}
              yUnit={data?.yUnit || ''}
              color={tooltipData.color}
            />
          );
        }}
      />
    </SectionWrapper>
  );
}

export const ReportingConsumptionTemperatureRoute = () => {
  const { t } = useTranslation('reporting');
  useRouteDocumentTitle(t('reporting.documentTitle'), ReportingPaths.ConsumptionTemperature);

  return (
    <DashboardFiltersProvider>
      <ReportPageContent
        breadcrumb={t('energyConsumptionTemperatureTitle')}
        menu={<DashboardFilters showOnlySpacesWithEosEnabled visiblePresets={eosDateRangeFilterVisiblePresets} />}
      >
        <ConsumptionEnergyByTemperatureSection />
      </ReportPageContent>
    </DashboardFiltersProvider>
  );
};
